/*
 * This code is the intellectual property of Bulb Inc. and other software publishers;
 * it may not be altered, copied or disclosed without prior written approval.
 * Copyright 2020, Bulb Inc. For more information, please email contact@hellobulb.com.
 *          _
 *  _            | | _
 * | |         _ | || |
 * | |__   _  | || || |__
 * | '_ \ | | | || || '_ \
 * | (_) || |_| || || (_) |
 *  \___/  \___/ |_| \___/
*/

import { Inject, Injectable } from '@angular/core';

import { Observable, Subject } from 'rxjs';

import { HttpClient } from '@angular/common/http';
import { AnalyticsService } from '../common/analytics.service';
import { DisplayProps } from '../app/avatar/types';
import { Resume } from './resume.model';

/**
 * Angular service that handles general resume actions.
 */
@Injectable()
export class ResumeService {
  /**
   * Resume tab is selected set as true else other tab set the value as false.
   */
  public isResumeTabSelected : boolean = false;

  /**
   * Contact details for resume is updated set the value as true.
   */
  public isResumeContactUpdated : boolean = false;

  /**
   * Update the resume name.
   */
  public updateResumeName = new Subject<string>();

  /**
   * Update the profile image.
   */
  public updateProfileImage = new Subject<any>();

  public canViewResume: boolean;

  /**
   * Default avatar props of user.
   * Used to show default avatar if no user profile image is uploaded.
   */
  public avatarDisplayProps: DisplayProps;

  /**
   * Default Constructor.
   *
   * @param bulbAuthService
   * @param analyticsService Our Google Tag Manager Analytics service.
   * @param http Http client.
   */
  constructor(
    @Inject('BulbAuthService') private bulbAuthService,
    private analyticsService : AnalyticsService,
    private http: HttpClient,
  ) {}

  /**
   * Create Resume for user.
   */
  createResume() {
    return this.http.post('resumes', {}) as Observable<Resume>;
  }

  /**
   * Method that returns if the user viewing the resume is the owner.
   */
  getIsOwner(resume : Resume) {
    return this.bulbAuthService.currentUser.hasPermission(resume, 'UPDATE');
  }

  /**
   * TODO TAM clean : This method really isn't needed anymore, since we are now using restangular objects as intended.
   * Leaving it in here for other code to work. Will remove later.
   *
   * ( Please don't remove this comment until the refactor has been 100% completed ).
   *
   * - files that still use this method :
   * - contact-info-dialog
   * - compositeview-controller.js
   *
   * ^^ These will have to be refactored. ^^
   *
   * Update Resume for user.
   *
   * @param resume
   * @param resumeId
   */
  updateResume(resume : Resume, resumeId : string) : Promise< any > {
    return this.http.put(`resumes/${resume.id}`, resume).toPromise();
  }

  /**
   * Fetch resume for current user.
   *
   * @param resumeId
   */
  fetchResume(resumeId : string) : Promise < any > {
    return this.http.get(`resumes/${resumeId}`).toPromise();
  }

  /**
   * Fetch resume token associated with the resume
   *
   * @param resumeId
   */
  fetchResumeToken(resumeId : string) : Promise < any > {
    return this.http.get(`resumes/${resumeId}/token`).toPromise();
  }

  /**
   * Generate the resume token
   *
   * @param resumeId
   */
  regenerateResumeToken(resumeId : string) : Promise < any > {
    return this.http.post(`resumes/${resumeId}/token`, {}).toPromise();
  }

  // /**
  //  * Calls account patch api to update the show resume intro model prefs for the account.
  //  */
  // updateAccountPrefs() {
  //   const currentUserPrefs = this.bulbAuthService.currentUser.prefs;
  //   currentUserPrefs.showResumeIntroModal = false;
  //   return this.restangular.one('accounts', this.bulbAuthService.currentUser.id)
  //     .patch({ prefs: currentUserPrefs }).toPromise();
  // }
}
